import { request as send } from '../../shared/http.js';

// Admin calls authenticate with the session cookie, which the browser sends on
// its own - it is HttpOnly, so nothing here reads or writes it.
const request = (path, options) => send(path, options, {}, 'include');

// The primary way in. One field and one call, whether the address has an
// account or not - the reply is identical either way on purpose, so this form
// cannot be used to ask whether somebody is registered. What differs arrives in
// the mailbox, which only its holder reads.
export const requestEmailLink = (email) =>
  request('/api/auth/email', { method: 'POST', body: JSON.stringify({ email }) });

// Unauthenticated by design, like redeemImpersonation below: this is what a
// link opened from a mailbox calls, in whatever browser that mailbox is being
// read in. A POST rather than the link itself being the endpoint, so a mail
// client that prefetches URLs cannot spend somebody's link on the way past.
export const redeemEmailLink = (token) =>
  request(`/api/auth/email/${encodeURIComponent(token)}`, { method: 'POST' });

// Secondary, for accounts that have chosen a password. An account created by
// link has none until it does.
export const login = (email, password) =>
  request('/api/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) });
export const logout = () => request('/api/auth/logout', { method: 'POST' });
export const fetchMe = () => request('/api/auth/me');

// Both take the current password again, even though the caller is signed in:
// a session is a cookie in a browser that may be sitting unlocked on a desk,
// and these are the two changes that would take an account away from its owner.
// An account with no password is asked for none, because there is none to give.
//
// changeEmail starts a move rather than making one - it answers with the
// address it sent a confirmation to, and nothing changes until that link is
// followed. A mistyped address then costs a message nobody reads, instead of
// stranding an account that may have no password to get back in with.
export const changeEmail = (email, password) =>
  request('/api/account/email', { method: 'POST', body: JSON.stringify({ email, password }) });

// Sends a link to the address the account already has, which is how an account
// created before any of this existed settles its unverified state.
export const sendVerificationEmail = () =>
  request('/api/account/email/verify', { method: 'POST' });
// What this account is called, which is the one thing on it that somebody who
// is not its owner ever reads: a shared display's link unfurls as "Claire's
// Litbyt" in whatever it was pasted into. No password, because a name is not a
// credential and changing it takes nothing away.
export const setAccountName = (name) =>
  request('/api/account/name', { method: 'POST', body: JSON.stringify({ name }) });

export const changePassword = (currentPassword, newPassword) =>
  request('/api/account/password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  });

export const fetchSettings = () => request('/api/settings');
export const saveSettings = (settings) =>
  request('/api/settings', { method: 'POST', body: JSON.stringify(settings) });

export const fetchProviders = () => request('/api/providers').then((data) => data.providers ?? []);
export const fetchConnections = () => request('/api/connections').then((data) => data.connections ?? []);
export const createConnection = (connection) =>
  request('/api/connections', { method: 'POST', body: JSON.stringify(connection) });
export const deleteConnection = (id) => request(`/api/connections/${id}`, { method: 'DELETE' });
export const syncConnection = (id) => request(`/api/connections/${id}/sync`, { method: 'POST' });

export const claimPairing = (code, name) =>
  request('/api/devices/claim', { method: 'POST', body: JSON.stringify({ code, name }) });
export const fetchDevices = () => request('/api/devices').then((data) => data.devices ?? []);
export const fetchDefaultDevice = () => request('/api/devices/default');
export const rotateDefaultDevice = () => request('/api/devices/default/rotate', { method: 'POST' });
export const deleteDevice = (id) => request(`/api/devices/${id}`, { method: 'DELETE' });

// The columns come from the server, defaults included, so the boxes ticked here
// and what a bare request gets cannot drift apart. The export itself answers
// with a file, not JSON - see options.as in shared/http.js.
export const fetchExportColumns = () =>
  request('/api/export/bookshelf/columns').then((data) => data.columns ?? []);
export const exportBookshelf = (columns) =>
  request(`/api/export/bookshelf?columns=${encodeURIComponent(columns.join(','))}`, { as: 'blob' });

// Site admin only. Every one of these is refused for an ordinary account and
// for a session opened through an impersonation link.
export const fetchAccounts = () => request('/api/admin/accounts').then((data) => data.accounts ?? []);
export const deleteAccount = (id) => request(`/api/admin/accounts/${id}`, { method: 'DELETE' });
export const impersonateAccount = (id) =>
  request(`/api/admin/accounts/${id}/impersonate`, { method: 'POST' });

// The Enrichment tab: books the Library of Congress answered with records
// that do not carry their ISBN, for a site admin to choose between. A decision
// answers 409 when the book changed since it was shown - decided by somebody
// else, or re-searched.
export const fetchEnrichment = (view) =>
  request(`/api/admin/enrichment${view && view !== 'pending' ? `?view=${encodeURIComponent(view)}` : ''}`).then(
    (data) => data.items ?? []
  );
export const chooseEnrichment = (key, recordId) =>
  request(`/api/admin/enrichment/${encodeURIComponent(key)}/choose`, {
    method: 'POST',
    body: JSON.stringify({ recordId }),
  });
export const rejectEnrichment = (key) =>
  request(`/api/admin/enrichment/${encodeURIComponent(key)}/reject`, { method: 'POST' });
export const reopenEnrichment = (key) =>
  request(`/api/admin/enrichment/${encodeURIComponent(key)}/reopen`, { method: 'POST' });

// The Goodreads tab: the session cookie every bookshelf is read through. It
// goes in and never comes back out - what comes back is whose it is and when
// it was set. Setting and checking both ask Goodreads, and answer 422 when
// Goodreads does not accept the cookie.
export const fetchGoodreadsSession = () => request('/api/admin/goodreads');
export const setGoodreadsSession = (cookie) =>
  request('/api/admin/goodreads', { method: 'POST', body: JSON.stringify({ cookie }) });
export const clearGoodreadsSession = () => request('/api/admin/goodreads', { method: 'DELETE' });
export const checkGoodreadsSession = () => request('/api/admin/goodreads/check', { method: 'POST' });

// Unauthenticated by design: this is what a link opened in another browser
// calls to trade its code for a session.
export const redeemImpersonation = (code) =>
  request(`/api/auth/impersonate/${encodeURIComponent(code)}`, { method: 'POST' });
